<template lang="pug">
section.section
  transition(name="fade")
    .container(v-if="post")
      .columns
        .column.is-offset-3-desktop.is-6-desktop.is-offset-2-tablet.is-8-tablet
          .notification.is-success.has-text-centered(v-if="showSuccess")
            | Posting has been successfully saved.

      h1.title {{ post.title }}

      template(v-if="isStaff")
        .buttons.has-addons
          .button.is-light(@click="previousPost")
            span.icon
              font-awesome-icon(icon="caret-left" size="2x")
            span Prev
          .button.is-success(:class="{'is-light': postStatus !== 'active'}" @click="updateStatus('active')") {{ approveText }}
          .button.is-danger(:class="{'is-light': postStatus !== 'rejected'}" @click="updateStatus('rejected')") {{ rejectText }}
          .button.is-light(@click="goToNextPost")
            span Next
            span.icon
              font-awesome-icon(icon="caret-right" size="2x")

      .notification.is-warning.is-light.is-shadowless(v-if="showStatus('pending')")
        p Your posting is currently in review for approval.  Check back soon for an update.
      .notification.is-success.is-light.is-shadowless(v-if="showStatus('active')")
        p Your posting has been approved and is currently published.
      .notification.is-danger.is-light.is-shadowless(v-if="showStatus('expired')")
        p Your posting has expired and is no longer published.
      .notification.is-danger.is-light.is-shadowless(v-if="showStatus('closed')")
        p Your posting has been closed and is no longer published.

      .columns
        .column.has-text-grey
          font-awesome-icon(icon="calendar")
          span  Posted: {{ post.created_at | prettyTime }}
        .column.has-text-right-tablet.has-text-grey(v-if="!isStaff")
          font-awesome-icon(icon="calendar-times")
          span  Expiring: {{ post.expire_at | prettyTime }}

      template(v-if="user")
        h3.title.is-4 User
        .user-tags
          .field.is-grouped.is-grouped-multiline
            .control
              .tags.has-addons.are-medium
                span.tag Email
                span.tag {{ user.email }}
            .control
              .tags.has-addons.are-medium
                span.tag Member Since
                span.tag {{ user.created_at | prettyShortTime }}

      h3.title.is-4 Description
      .content
        p {{ post.description }}

      h3.title.is-4 Other Resources
      .content
        p {{ post.other_providers }}

      h3.title.is-4 Requested By
      .content
        p {{ post.when }}

      p(v-if="showReplyButton")
        button.button.is-primary(@click="contact" :class="{'is-loading': contactLoading}") Reply
      template(v-if="postContact")
        h3.title.is-4 Contact
        .content
          p {{ postContact.contact }}
</template>

<script>
import POST_SHOW from '@/graphql/posts/find.gql'
import POST_CONTACT from '@/graphql/posts/contact.gql'
import POST_STATUS from '@/graphql/posts/updateStatus.gql'
import POSTER from '@/graphql/users/find.gql'
import NEXT_POST from '@/graphql/posts/next.gql'
import moment from 'moment'

export default {
  data () {
    return {
      post: null,
      user: null,
      postContact: null,
      nextPost: null,
      contacting: false,
      postStatus: null
    }
  },

  computed: {
    postId () {
      return parseInt(this.$route.params.id)
    },
    contactLoading () {
      return this.contactCounter > 0
    },
    showSuccess () {
      return this.$route.params.status === 'success'
    },
    isStaff () {
      return this.$auth.has('staff')
    },
    showReplyButton () {
      return !this.postContact && !this.$auth.has('staff') &&
        (!this.$auth.user || this.$auth.user.id !== this.post.user_id)
    },
    approveText () {
      return this.postStatus === 'active' ? 'Approved' : 'Approve'
    },
    rejectText () {
      return this.postStatus === 'rejected' ? 'Rejected' : 'Reject'
    }
  },

  filters: {
    prettyTime (time) {
      return moment(time).format('dddd, MMMM Do YYYY')
    },
    prettyShortTime (time) {
      return moment(time).format('MMMM D, YYYY')
    }
  },

  apollo: {
    post: {
      fetchPolicy: 'no-cache', // TODO: ensure we are updating the cache
      query: POST_SHOW,
      variables () {
        return {
          id: this.postId
        }
      },
      update (data) {
        const post = data.posts_by_pk
        if (post) this.postStatus = post.status
        return post
      }
    },
    postContact: {
      query: POST_CONTACT,
      loadingKey: 'contactCounter',
      skip () {
        return !this.contacting && !this.$auth.has('staff')
      },
      variables () {
        return { id: this.postId }
      },
      update (data) {
        return data.posts_by_pk
      }
    },
    user: {
      query: POSTER,
      skip () {
        return !this.post || !this.$auth.has('staff')
      },
      variables () {
        return {
          id: this.post.user_id
        }
      },
      update (data) {
        return data.users_by_pk
      }
    },
    nextPost: {
      query: NEXT_POST,
      skip () {
        return !this.postId || !this.$auth.has('staff')
      },
      variables () {
        return { id: this.postId }
      },
      update ({ posts }) {
        return posts && posts[0]
      }
    }
  },

  methods: {
    contact () {
      if (this.$auth.user) {
        this.contacting = true
      } else {
        this.$auth.login()
      }
    },
    showStatus (status) {
      if (!this.$auth.user || !this.post || this.$auth.user.id !== this.post.user_id) {
        return false
      }
      const expiring = this.post && moment(this.post.expire_at)
      if (status === 'expired') {
        return expiring < new Date()
      } else if (status === 'pending') {
        return expiring > new Date() && this.post.status === 'pending'
      } else if (status === 'active') {
        return expiring > new Date() && this.post.status === 'active'
      } else if (status === 'closed') {
        return this.post.status === 'closed' || this.post.status === 'rejected'
      }
      return false
    },
    updateStatus (status) {
      return this.$apollo.mutate({
        mutation: POST_STATUS,
        variables: { id: this.post.id, status: status },
        loadingKey: 'savingCounter'
      }).then(result => {
        this.postStatus = status
      }).catch(error => console.log(error))
    },
    previousPost () {
      this.$router.go(-1)
    },
    goToNextPost () {
      if (this.nextPost) {
        this.$router.push({ name: 'post', params: { id: this.nextPost.id } })
      } else {
        this.$router.push({ name: 'manage' })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.content {
  padding-top: 0;
}
.user-tags {
  margin-top: -0.5em;
  margin-bottom: 1.2em;
}
</style>
